import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { toast } from 'react-toastify'

import {
    completeBuilder,
    completeUpload,
    requestBuilderToken,
    requestUploadUrl,
    saveBuilderDraft,
    uploadFileToS3,
    useSignableDoc,
    useSignableDocs,
    useStandardSignableTemplates,
    useSuggestedPrefillFields,
    provisionStandardSignableDoc,
    provisionSignableDocFromCatalog,
    useSignableDocCatalog,
} from '../../hooks/Outreach/useSignableDocs'
import { useAptConfig } from '../../hooks/useAptConfig'
import {
    SIGNABLE_DOC_TYPES,
    SIGNABLE_DOC_TYPE_LABELS,
    SignableDocType,
} from '../../models/Outreach/types'
import { Routes } from '../../navigation/config/routes'
import { SignableDocBuilderGuideButton } from './SignableDocBuilderGuide'
import {
    DOCUSEAL_BUILDER_SUBMITTER_ROLES,
    DocuSealBuilderEmbed,
} from './DocuSealBuilderEmbed'
import { builderFieldsForEnabled } from './SignableDocFieldPickerDialog'

type Phase = 'build' | 'details'
type UploadStatus = 'idle' | 'uploading' | 'ready' | 'error'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2, 3, 3),
        maxWidth: 1400,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px)',
    },
    rootFullWidth: {
        maxWidth: 'none',
        margin: 0,
        marginLeft: -24,
        marginRight: -24,
        width: 'calc(100% + 48px)',
        padding: theme.spacing(0, 1, 1),
    },
    rootBuilderViewport: {
        height: 'calc(100vh - 56px - 8px)',
        maxHeight: 'calc(100vh - 56px - 8px)',
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    header: {
        marginBottom: theme.spacing(2),
        flexShrink: 0,
    },
    headerBuilder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        flexShrink: 0,
        minHeight: 40,
    },
    headerBuilderActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    buildWorkspace: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    buildWorkspaceFixed: {
        overflow: 'hidden',
    },
    metaRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 280px',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    choiceRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    choicePanel: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 240,
    },
    uploadZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        textAlign: 'center',
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
    uploadZoneDisabled: {
        cursor: 'default',
        opacity: 0.7,
        '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
        },
    },
    buildChoices: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        maxWidth: 960,
    },
    builderRow: {
        flex: 1,
        display: 'flex',
        minHeight: 0,
        overflow: 'hidden',
    },
    builderMain: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    detailsForm: {
        maxWidth: 560,
        marginTop: theme.spacing(2),
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        flexShrink: 0,
    },
    hiddenInput: {
        display: 'none',
    },
}))

interface CreateLocationState {
    docId?: number
    documentUrls?: string[]
    documentType?: SignableDocType
    useStandard?: boolean
}

function nameFromFilename(filename: string): string {
    return filename
        .replace(/\.(pdf|docx)$/i, '')
        .replace(/[-_]+/g, ' ')
        .trim()
}

export const SignableDocsCreate: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation<CreateLocationState>()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const uploadStartedRef = useRef(false)
    const standardProvisionStartedRef = useRef(false)

    const resumeDocId = location.state?.docId
    const resumeDocumentUrls = location.state?.documentUrls
    const navDocumentType = location.state?.documentType
    const navUseStandard = location.state?.useStandard ?? false
    const isCopyFlow = Boolean(resumeDocId && resumeDocumentUrls?.length)

    const [phase, setPhase] = useState<Phase>('build')
    const [docId, setDocId] = useState<number | null>(resumeDocId ?? null)
    const [name, setName] = useState('')
    const [documentType, setDocumentType] = useState<SignableDocType>(
        navDocumentType && navDocumentType !== 'APPLICATION'
            ? navDocumentType
            : 'LEASE',
    )
    const [documentUrl, setDocumentUrl] = useState<string | null>(
        resumeDocumentUrls?.[0] ?? null,
    )
    const [unitConfigId, setUnitConfigId] = useState<number | ''>('')
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
        isCopyFlow ? 'ready' : 'idle',
    )
    const [builderToken, setBuilderToken] = useState<{
        host: string
        token: string
    } | null>(null)
    const [builderLoading, setBuilderLoading] = useState(false)
    const [builderError, setBuilderError] = useState<string | null>(null)
    const [savedTemplate, setSavedTemplate] = useState<{
        templateId: number
        slug?: string
    } | null>(null)
    /** True only after the PM clicks Save in the DocuSeal builder (not on API template create). */
    const [builderSaved, setBuilderSaved] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [enabledFieldsLocal, setEnabledFieldsLocal] = useState<string[]>([])
    const [isStandardProvision, setIsStandardProvision] = useState(false)

    const { createDoc, patchDoc } = useSignableDocs()
    const existingDocQuery = useSignableDoc(docId ?? undefined)
    const suggestedFieldsQuery = useSuggestedPrefillFields()
    const standardTemplatesQuery = useStandardSignableTemplates()
    const catalogQuery = useSignableDocCatalog({ pipeline_eligible: true })
    const { unitConfigList } = useAptConfig({ unitConfigList: true })

    const configuredEnabledFields = useMemo(() => {
        if (enabledFieldsLocal.length > 0) {
            return enabledFieldsLocal
        }
        if (existingDocQuery.data?.enabled_fields?.length) {
            return existingDocQuery.data.enabled_fields
        }
        return (
            suggestedFieldsQuery.data?.builder_fields.map(
                (field) => field.name,
            ) ?? []
        )
    }, [
        enabledFieldsLocal,
        existingDocQuery.data?.enabled_fields,
        suggestedFieldsQuery.data?.builder_fields,
    ])

    const builderDefaultFields = useMemo(
        () =>
            builderFieldsForEnabled(
                suggestedFieldsQuery.data?.builder_fields ?? [],
                configuredEnabledFields,
            ),
        [suggestedFieldsQuery.data?.builder_fields, configuredEnabledFields],
    )

    const catalogTemplates = useMemo(
        () =>
            (catalogQuery.data ?? []).filter(
                (entry) =>
                    entry.available && entry.document_type === documentType,
            ),
        [catalogQuery.data, documentType],
    )

    const canStartDocument = Boolean(name.trim() && documentType)

    const standardNaaTemplate = useMemo(
        () =>
            standardTemplatesQuery.data?.templates.find(
                (template) => template.key === 'naa_application',
            ),
        [standardTemplatesQuery.data?.templates],
    )

    const builderFieldsReady =
        suggestedFieldsQuery.isSuccess && configuredEnabledFields.length > 0

    useEffect(() => {
        const doc = existingDocQuery.data
        if (!doc) return
        if (doc.name) setName(doc.name)
        setDocumentType(doc.document_type)
        if (doc.unit_config?.id) {
            setUnitConfigId(doc.unit_config.id)
        }
        if (doc.enabled_fields?.length) {
            setEnabledFieldsLocal(doc.enabled_fields)
        }
    }, [existingDocQuery.data])

    const loadBuilderToken = useCallback(
        async (id: number, urls?: string[], templateId?: number) => {
            setBuilderLoading(true)
            setBuilderError(null)
            try {
                const payload =
                    templateId != null
                        ? { template_id: templateId }
                        : {
                              document_urls:
                                  urls ?? (documentUrl ? [documentUrl] : []),
                          }
                const data = await requestBuilderToken(id, payload)
                setBuilderToken(data)
            } catch (error: unknown) {
                const message =
                    (error as { response?: { data?: { error?: string } } })
                        ?.response?.data?.error ?? 'Failed to load builder'
                setBuilderError(message)
            } finally {
                setBuilderLoading(false)
            }
        },
        [documentUrl],
    )

    const startBuilder = useCallback(
        (id: number, url: string, templateId?: number) => {
            setBuilderToken(null)
            setBuilderSaved(false)
            if (templateId != null) {
                loadBuilderToken(id, undefined, templateId)
            } else {
                loadBuilderToken(id, [url])
            }
        },
        [loadBuilderToken],
    )

    const requireNameAndType = useCallback(() => {
        if (!name.trim()) {
            toast.error('Name is required')
            return false
        }
        if (!documentType) {
            toast.error('Document type is required')
            return false
        }
        return true
    }, [name, documentType])

    const handleProvisionCatalogTemplate = useCallback(
        async (catalogTemplateKey: string, displayName: string) => {
            if (!requireNameAndType()) return
            setUploadStatus('uploading')
            setBuilderToken(null)
            setBuilderSaved(false)
            setIsStandardProvision(true)
            try {
                const result = await provisionSignableDocFromCatalog({
                    catalog_template_key: catalogTemplateKey,
                    name: name.trim() || displayName,
                })
                setDocId(result.id)
                if (result.name) setName(result.name)
                if (result.document_type) setDocumentType(result.document_type)
                setPhase('build')
                startBuilder(result.id, '', result.template_id)
            } catch (error: unknown) {
                const message =
                    (error as { response?: { data?: { error?: string } } })
                        ?.response?.data?.error ??
                    'Failed to provision catalog template'
                toast.error(message)
                setUploadStatus('error')
            } finally {
                setIsStandardProvision(false)
                setUploadStatus('idle')
            }
        },
        [name, requireNameAndType, startBuilder],
    )

    const handleUseStandardTemplate = useCallback(async () => {
        if (!requireNameAndType()) return
        const catalogEntry = catalogTemplates[0]
        if (catalogEntry) {
            await handleProvisionCatalogTemplate(
                catalogEntry.template_key,
                catalogEntry.display_name,
            )
            return
        }
        if (!standardNaaTemplate?.available) {
            toast.error('Standard application template is not configured yet.')
            return
        }
        setUploadStatus('uploading')
        setBuilderToken(null)
        setSavedTemplate(null)
        setBuilderSaved(false)
        setIsStandardProvision(true)
        try {
            const result = await provisionStandardSignableDoc({
                template_key: 'naa_application',
                name: name.trim(),
            })
            setDocId(result.id)
            if (result.name) setName(result.name)
            if (result.enabled_fields?.length) {
                setEnabledFieldsLocal(result.enabled_fields)
            }
            setUploadStatus('ready')
            const templateId = result.template_id ?? result.docuseal_template_id
            startBuilder(result.id, '', templateId ?? undefined)
        } catch (error: unknown) {
            const message =
                (error as { response?: { data?: { error?: string } } })
                    ?.response?.data?.error ??
                'Failed to load standard template'
            toast.error(message)
            setUploadStatus('idle')
            setIsStandardProvision(false)
        }
    }, [
        catalogTemplates,
        handleProvisionCatalogTemplate,
        name,
        requireNameAndType,
        standardNaaTemplate?.available,
        startBuilder,
    ])

    useEffect(() => {
        if (!navUseStandard || standardProvisionStartedRef.current) return
        if (!standardNaaTemplate?.available) return
        if (!name.trim()) return
        standardProvisionStartedRef.current = true
        void handleUseStandardTemplate()
    }, [
        navUseStandard,
        name,
        standardNaaTemplate?.available,
        handleUseStandardTemplate,
    ])

    const handleFileUpload = useCallback(
        async (file: File, replace = false) => {
            if (!requireNameAndType()) return
            if (!replace && uploadStartedRef.current) return
            uploadStartedRef.current = true
            setUploadStatus('uploading')
            setBuilderToken(null)
            setSavedTemplate(null)
            setBuilderSaved(false)

            const draftName =
                name.trim() ||
                nameFromFilename(file.name) ||
                'Untitled document'
            if (!name.trim()) {
                setName(draftName)
            }

            try {
                let id = docId
                if (!id) {
                    const created = await createDoc({
                        name: draftName,
                        document_type: documentType,
                    })
                    if (!created?.id) {
                        toast.error(
                            'Document was created but no ID was returned',
                        )
                        setUploadStatus('error')
                        uploadStartedRef.current = false
                        return
                    }
                    id = created.id
                    setDocId(id)
                    if (created.enabled_fields?.length) {
                        setEnabledFieldsLocal(created.enabled_fields)
                    }
                }

                const uploadMeta = await requestUploadUrl(
                    id,
                    file.name,
                    file.type || undefined,
                )
                await uploadFileToS3(
                    uploadMeta.upload_url,
                    file,
                    uploadMeta.content_type,
                )
                const completed = await completeUpload(id, uploadMeta.s3_key)
                setDocumentUrl(completed.document_url)
                setUploadStatus('ready')
                startBuilder(id, completed.document_url)
            } catch {
                toast.error('Upload failed')
                setUploadStatus('error')
                uploadStartedRef.current = false
            }
        },
        [
            docId,
            createDoc,
            startBuilder,
            name,
            documentType,
            requireNameAndType,
        ],
    )

    const handleFileInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0]
        if (file) {
            handleFileUpload(file, false)
        }
        event.target.value = ''
    }

    useEffect(() => {
        if (!isCopyFlow || !docId || builderToken || builderLoading) return
        const urls = resumeDocumentUrls ?? []
        if (urls.length === 0) return

        const templateId = existingDocQuery.data?.docuseal_template_id
        if (templateId) {
            startBuilder(docId, urls[0], templateId)
        } else {
            startBuilder(docId, urls[0])
        }
    }, [
        isCopyFlow,
        docId,
        resumeDocumentUrls,
        builderToken,
        builderLoading,
        existingDocQuery.data?.docuseal_template_id,
        startBuilder,
    ])

    const handleBuilderSave = async (payload: {
        templateId: number
        slug?: string
    }) => {
        if (!docId) return
        try {
            await saveBuilderDraft(docId, {
                docuseal_template_id: payload.templateId,
                docuseal_slug: payload.slug,
            })
            setSavedTemplate({
                templateId: payload.templateId,
                slug: payload.slug,
            })
            setBuilderSaved(true)
        } catch {
            toast.error('Failed to save template')
        }
    }

    const handleContinueToDetails = () => {
        if (!builderSaved || !savedTemplate) {
            toast.error('Save your fields in the builder before continuing')
            return
        }
        setPhase('details')
    }

    const handleFinish = async () => {
        if (!docId || !savedTemplate) return
        if (!name.trim()) {
            toast.error('Name is required')
            return
        }
        setIsSubmitting(true)
        try {
            await patchDoc({
                id: docId,
                payload: {
                    name: name.trim(),
                    document_type: documentType,
                    unit_config_id: unitConfigId === '' ? null : unitConfigId,
                },
            })
            await completeBuilder(docId, {
                docuseal_template_id: savedTemplate.templateId,
                docuseal_slug: savedTemplate.slug,
            })
            history.push(
                Routes.manage.signableDocs.detail.replace(':id', String(docId)),
            )
        } catch {
            toast.error('Failed to finish setup')
        } finally {
            setIsSubmitting(false)
        }
    }

    const showBuilder =
        uploadStatus === 'ready' &&
        docId != null &&
        (documentUrl != null || isCopyFlow || isStandardProvision)

    const builderOpen = phase === 'build' && showBuilder

    useEffect(() => {
        if (!builderOpen) return undefined
        const mainEl = document.querySelector('main')
        if (!(mainEl instanceof HTMLElement)) return undefined
        const previousOverflow = mainEl.style.overflow
        mainEl.style.overflow = 'hidden'
        return () => {
            mainEl.style.overflow = previousOverflow
        }
    }, [builderOpen])

    return (
        <Box
            className={`${classes.root} ${
                builderOpen ? classes.rootFullWidth : ''
            } ${builderOpen ? classes.rootBuilderViewport : ''}`}
        >
            {builderOpen ? (
                <Box>
                    <Box className={classes.headerBuilder}>
                        <Button
                            component={Link}
                            to={`${Routes.manage.contractManager}?tab=documents`}
                            startIcon={<ArrowBackIcon />}
                            size="small"
                        >
                            Back to Documents
                        </Button>
                        <Box className={classes.headerBuilderActions}>
                            <SignableDocBuilderGuideButton />
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleContinueToDetails}
                                disabled={!builderSaved || builderLoading}
                            >
                                Done placing fields
                            </Button>
                        </Box>
                    </Box>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        Use a different party for each person, and name each
                        field something you will recognize when you map it.
                    </Typography>
                </Box>
            ) : (
                <Box className={classes.header}>
                    <Button
                        component={Link}
                        to={`${Routes.manage.contractManager}?tab=documents`}
                        startIcon={<ArrowBackIcon />}
                        size="small"
                    >
                        Back to Documents
                    </Button>
                    <Typography
                        variant="h5"
                        gutterBottom
                        style={{ marginTop: 8 }}
                    >
                        {phase === 'build'
                            ? 'New signable document'
                            : 'Document details'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {phase === 'build'
                            ? 'Name the document, choose its type, then start from a template or upload a PDF.'
                            : 'Confirm details and choose where this document applies.'}
                    </Typography>
                </Box>
            )}

            {phase === 'build' && (
                <Box
                    className={`${classes.buildWorkspace} ${
                        builderOpen ? classes.buildWorkspaceFixed : ''
                    }`}
                >
                    {!showBuilder && (
                        <Box className={classes.buildChoices}>
                            <Box className={classes.metaRow}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    variant="outlined"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    disabled={uploadStatus === 'uploading'}
                                />
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    required
                                    disabled={
                                        uploadStatus === 'uploading' ||
                                        isCopyFlow
                                    }
                                >
                                    <InputLabel id="doc-type-label">
                                        Document type
                                    </InputLabel>
                                    <Select
                                        labelId="doc-type-label"
                                        label="Document type"
                                        value={documentType}
                                        onChange={(e) =>
                                            setDocumentType(
                                                e.target
                                                    .value as SignableDocType,
                                            )
                                        }
                                    >
                                        {SIGNABLE_DOC_TYPES.filter(
                                            (type) => type !== 'APPLICATION',
                                        ).map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {SIGNABLE_DOC_TYPE_LABELS[type]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box className={classes.choiceRow}>
                                <Box className={classes.choicePanel}>
                                    <Typography variant="h6">
                                        Start from template
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        paragraph
                                    >
                                        Provision from the EZOS catalog for this
                                        document type.
                                    </Typography>
                                    {catalogTemplates.map((entry) => (
                                        <Box key={entry.template_key} mb={1}>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={() =>
                                                    handleProvisionCatalogTemplate(
                                                        entry.template_key,
                                                        entry.display_name,
                                                    )
                                                }
                                                disabled={
                                                    uploadStatus ===
                                                        'uploading' ||
                                                    !canStartDocument
                                                }
                                            >
                                                {entry.display_name}
                                            </Button>
                                        </Box>
                                    ))}
                                    {catalogTemplates.length === 0 && (
                                        <>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                paragraph
                                            >
                                                {documentType === 'APPLICATION'
                                                    ? standardNaaTemplate?.description ??
                                                      'Pre-tagged application with EZOS field names.'
                                                    : `No catalog templates for ${SIGNABLE_DOC_TYPE_LABELS[documentType]} yet.`}
                                            </Typography>
                                            {documentType === 'APPLICATION' && (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    onClick={
                                                        handleUseStandardTemplate
                                                    }
                                                    disabled={
                                                        uploadStatus ===
                                                            'uploading' ||
                                                        !canStartDocument ||
                                                        !standardNaaTemplate?.available
                                                    }
                                                >
                                                    {uploadStatus ===
                                                        'uploading' &&
                                                    isStandardProvision ? (
                                                        <CircularProgress
                                                            size={22}
                                                        />
                                                    ) : (
                                                        'Use standard application'
                                                    )}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    {!standardNaaTemplate?.available &&
                                        catalogTemplates.length === 0 &&
                                        documentType === 'APPLICATION' &&
                                        !standardTemplatesQuery.isLoading &&
                                        !catalogQuery.isLoading && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                                style={{ marginTop: 8 }}
                                            >
                                                Not configured in this
                                                environment yet.
                                            </Typography>
                                        )}
                                    {standardNaaTemplate?.available &&
                                        documentType === 'APPLICATION' &&
                                        standardNaaTemplate.pdf_configured &&
                                        standardNaaTemplate.pdf_ready ===
                                            false && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                display="block"
                                                style={{ marginTop: 8 }}
                                            >
                                                PDF not found in S3 at{' '}
                                                <strong>
                                                    {
                                                        standardNaaTemplate.pdf_s3_key
                                                    }
                                                </strong>
                                                .
                                            </Typography>
                                        )}
                                </Box>

                                <Box className={classes.choicePanel}>
                                    <Typography variant="h6">
                                        Upload PDF
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        paragraph
                                    >
                                        Upload your own PDF or DOCX, then place
                                        fields in the builder.
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        className={classes.hiddenInput}
                                        onChange={handleFileInputChange}
                                        disabled={uploadStatus === 'uploading'}
                                    />
                                    <Box
                                        className={`${classes.uploadZone} ${
                                            uploadStatus === 'uploading' ||
                                            !canStartDocument
                                                ? classes.uploadZoneDisabled
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (
                                                uploadStatus !== 'uploading' &&
                                                canStartDocument
                                            ) {
                                                fileInputRef.current?.click()
                                            } else if (!canStartDocument) {
                                                requireNameAndType()
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' &&
                                                uploadStatus !== 'uploading' &&
                                                canStartDocument
                                            ) {
                                                fileInputRef.current?.click()
                                            }
                                        }}
                                    >
                                        {uploadStatus === 'uploading' &&
                                        !isStandardProvision ? (
                                            <>
                                                <CircularProgress size={40} />
                                                <Typography
                                                    variant="body1"
                                                    style={{ marginTop: 16 }}
                                                >
                                                    Uploading and opening
                                                    builder…
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <CloudUploadIcon
                                                    style={{
                                                        fontSize: 48,
                                                        opacity: 0.5,
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Upload PDF or DOCX
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {canStartDocument
                                                        ? 'Click to choose a file'
                                                        : 'Enter a name and type first'}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {isCopyFlow && !showBuilder && uploadStatus === 'ready' && (
                        <Box className={classes.statusRow}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="textSecondary">
                                Opening builder from copied template…
                            </Typography>
                        </Box>
                    )}

                    {showBuilder && (
                        <Box className={classes.builderRow}>
                            <Box className={classes.builderMain}>
                                <DocuSealBuilderEmbed
                                    host={builderToken?.host ?? ''}
                                    token={builderToken?.token ?? ''}
                                    onSave={handleBuilderSave}
                                    isLoading={
                                        builderLoading ||
                                        !builderToken ||
                                        !builderFieldsReady
                                    }
                                    error={builderError}
                                    autosave={false}
                                    fullHeight
                                    compact
                                    hideSendButton
                                    hideSignYourselfButton
                                    helperText={null}
                                    submitterRoles={
                                        DOCUSEAL_BUILDER_SUBMITTER_ROLES
                                    }
                                    defaultFields={builderDefaultFields}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {phase === 'details' && (
                <>
                    <Box className={classes.detailsForm}>
                        <TextField
                            fullWidth
                            label="Document name"
                            variant="outlined"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            margin="normal"
                            required
                        />
                        <FormControl
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        >
                            <InputLabel id="doc-type-label">
                                Document type
                            </InputLabel>
                            <Select
                                labelId="doc-type-label"
                                label="Document type"
                                value={documentType}
                                onChange={(e) =>
                                    setDocumentType(
                                        e.target.value as SignableDocType,
                                    )
                                }
                                disabled={isCopyFlow}
                            >
                                {SIGNABLE_DOC_TYPES.filter(
                                    (type) => type !== 'APPLICATION',
                                ).map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {SIGNABLE_DOC_TYPE_LABELS[type]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        >
                            <InputLabel id="unit-config-label">
                                Unit type (optional)
                            </InputLabel>
                            <Select
                                labelId="unit-config-label"
                                label="Unit type (optional)"
                                value={unitConfigId}
                                onChange={(e) =>
                                    setUnitConfigId(
                                        e.target.value as number | '',
                                    )
                                }
                            >
                                <MenuItem value="">All unit types</MenuItem>
                                {unitConfigList.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 16 }}
                        >
                            After finishing, bind this document on the matching
                            step in Leasing Season Config.
                        </Typography>
                    </Box>

                    <Box className={classes.footer}>
                        <Button onClick={() => setPhase('build')}>
                            Back to builder
                        </Button>
                        <Box>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleFinish}
                                disabled={isSubmitting || !name.trim()}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    'Finish'
                                )}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}
